import { propsApi, PropInstance, assetsApi } from './api';

/**
 * Manages prop instances for a map
 * Handles loading, saving, and manipulating props
 */
export class PropManager {
  private mapId: string;
  private props: Map<string, PropInstance> = new Map();
  private propImages: Map<string, HTMLImageElement> = new Map();
  private loadedRegions: Set<string> = new Set();
  private dirtyProps: Set<string> = new Set();
  private newProps: Set<string> = new Set(); // Props created locally, not yet saved to server
  private deletedProps: Set<string> = new Set(); // Props marked for deletion
  private saveTimeout: NodeJS.Timeout | null = null;
  private onPropsChanged: (() => void) | null = null;
  private onSaveStateChanged: ((isSaving: boolean, hasUnsaved: boolean) => void) | null = null;
  private onBeforeSave: (() => Promise<void>) | null = null;
  private isSaving: boolean = false;
  private autoSaveEnabled: boolean = true;
  private nextLocalId: number = 1; // Counter for generating local IDs

  private static readonly SAVE_DELAY = 2000;
  private static readonly REGION_SIZE = 2048;

  constructor(mapId: string) {
    this.mapId = mapId;
  }

  /**
   * Set callback for when props change
   */
  setOnPropsChanged(callback: (() => void) | null): void {
    this.onPropsChanged = callback;
  }

  /**
   * Set callback for when save state changes
   */
  setOnSaveStateChanged(callback: ((isSaving: boolean, hasUnsaved: boolean) => void) | null): void {
    this.onSaveStateChanged = callback;
  }

  /**
   * Set callback to run before saving (e.g., to save layers first)
   */
  setOnBeforeSave(callback: (() => Promise<void>) | null): void {
    this.onBeforeSave = callback;
  }

  /**
   * Enable or disable autosave
   */
  setAutoSaveEnabled(enabled: boolean): void {
    this.autoSaveEnabled = enabled;
    // If disabling autosave, cancel any pending save
    if (!enabled && this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }

  /**
   * Notify save state changed
   */
  private notifySaveStateChanged(): void {
    const hasUnsaved = this.dirtyProps.size > 0 || this.newProps.size > 0 || this.deletedProps.size > 0;
    this.onSaveStateChanged?.(this.isSaving, hasUnsaved);
  }

  /**
   * Load all props for the map
   */
  async loadAllProps(): Promise<void> {
    try {
      const response = await propsApi.list(this.mapId);
      this.props.clear();

      for (const prop of response.props) {
        this.props.set(prop.id, prop);
      }

      this.notifyPropsChanged();
    } catch (err) {
      console.error('Failed to load props:', err);
    }
  }

  /**
   * Load props for a specific region
   */
  async loadRegionProps(regionX: number, regionY: number): Promise<void> {
    const key = `${regionX},${regionY}`;
    if (this.loadedRegions.has(key)) return;

    try {
      const response = await propsApi.getRegionProps(this.mapId, regionX, regionY);
      this.loadedRegions.add(key);

      for (const prop of response.props) {
        this.props.set(prop.id, { ...prop, regionX, regionY });
      }

      this.notifyPropsChanged();
    } catch (err) {
      console.error(`Failed to load props for region ${regionX},${regionY}:`, err);
    }
  }

  /**
   * Ensure props are loaded for visible regions
   * Limited to max 100 regions to avoid flooding the server at extreme zoom-out
   */
  async ensureRegionsLoaded(
    viewportX: number,
    viewportY: number,
    viewportWidth: number,
    viewportHeight: number,
    zoom: number
  ): Promise<void> {
    const halfWidth = viewportWidth / (2 * zoom);
    const halfHeight = viewportHeight / (2 * zoom);

    const minX = viewportX - halfWidth;
    const maxX = viewportX + halfWidth;
    const minY = viewportY - halfHeight;
    const maxY = viewportY + halfHeight;

    let minRegionX = Math.floor(minX / PropManager.REGION_SIZE);
    let maxRegionX = Math.floor(maxX / PropManager.REGION_SIZE);
    let minRegionY = Math.floor(minY / PropManager.REGION_SIZE);
    let maxRegionY = Math.floor(maxY / PropManager.REGION_SIZE);

    // Limit to max 10 regions per axis (100 total) to avoid flooding server
    const MAX_REGIONS_PER_AXIS = 10;
    const regionsX = maxRegionX - minRegionX + 1;
    const regionsY = maxRegionY - minRegionY + 1;

    if (regionsX > MAX_REGIONS_PER_AXIS) {
      const centerRegionX = Math.floor(viewportX / PropManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionX = centerRegionX - halfRegions;
      maxRegionX = centerRegionX + halfRegions;
    }

    if (regionsY > MAX_REGIONS_PER_AXIS) {
      const centerRegionY = Math.floor(viewportY / PropManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionY = centerRegionY - halfRegions;
      maxRegionY = centerRegionY + halfRegions;
    }

    const loadPromises: Promise<void>[] = [];

    for (let rx = minRegionX; rx <= maxRegionX; rx++) {
      for (let ry = minRegionY; ry <= maxRegionY; ry++) {
        loadPromises.push(this.loadRegionProps(rx, ry));
      }
    }

    await Promise.all(loadPromises);
  }

  /**
   * Load prop images
   */
  async loadPropImages(propAssets: { name: string }[]): Promise<void> {
    const loadPromises = propAssets.map(
      (asset) =>
        new Promise<void>((resolve) => {
          if (this.propImages.has(asset.name)) {
            resolve();
            return;
          }

          const img = new Image();
          img.onload = () => {
            this.propImages.set(asset.name, img);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = assetsApi.getFileUrl('props', asset.name);
        })
    );

    await Promise.all(loadPromises);
  }

  /**
   * Get a prop image
   */
  getPropImage(propName: string): HTMLImageElement | undefined {
    return this.propImages.get(propName);
  }

  /**
   * Get all prop images
   */
  getPropImages(): Map<string, HTMLImageElement> {
    return this.propImages;
  }

  /**
   * Get all props sorted by zIndex
   */
  getProps(): PropInstance[] {
    return Array.from(this.props.values()).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  }

  /**
   * Get a single prop by ID
   */
  getProp(propId: string): PropInstance | undefined {
    return this.props.get(propId);
  }

  /**
   * Add a new prop locally (will be saved on next save)
   */
  addProp(propData: {
    propName: string;
    x: number;
    y: number;
    scale?: number;
    rotation?: number;
    layerId?: string;
  }): PropInstance {
    // Generate a local ID (will be replaced by server ID on save)
    const localId = `local_${Date.now()}_${this.nextLocalId++}`;

    // Calculate region
    const regionX = Math.floor(propData.x / PropManager.REGION_SIZE);
    const regionY = Math.floor(propData.y / PropManager.REGION_SIZE);

    // Create the prop locally
    const prop: PropInstance = {
      id: localId,
      propName: propData.propName,
      x: propData.x,
      y: propData.y,
      scale: propData.scale ?? 1,
      rotation: propData.rotation ?? 0,
      zIndex: this.getMaxZIndex() + 1,
      layerId: propData.layerId ?? 'default',
      regionX,
      regionY
    };

    this.props.set(localId, prop);
    this.newProps.add(localId);
    this.scheduleSave();
    this.notifyPropsChanged();
    this.notifySaveStateChanged();

    return prop;
  }

  /**
   * Update a prop locally (with debounced save)
   */
  updatePropLocal(propId: string, updates: Partial<PropInstance>): void {
    const prop = this.props.get(propId);
    if (!prop) return;

    Object.assign(prop, updates);
    this.dirtyProps.add(propId);
    this.scheduleSave();
    this.notifyPropsChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Update a prop immediately
   */
  async updateProp(propId: string, updates: Partial<PropInstance>): Promise<PropInstance | null> {
    try {
      const response = await propsApi.update(this.mapId, propId, updates);
      const prop = { ...response.prop, regionX: response.regionX, regionY: response.regionY };
      this.props.set(propId, prop);
      this.dirtyProps.delete(propId);
      this.notifyPropsChanged();
      return prop;
    } catch (err) {
      console.error('Failed to update prop:', err);
      return null;
    }
  }

  /**
   * Delete a prop locally (will be deleted on server on next save)
   */
  deleteProp(propId: string): boolean {
    if (!this.props.has(propId)) return false;

    // Remove from local state
    this.props.delete(propId);
    this.dirtyProps.delete(propId);

    // If it was a new prop (not yet on server), just remove from newProps
    if (this.newProps.has(propId)) {
      this.newProps.delete(propId);
    } else {
      // It exists on server, mark for deletion
      this.deletedProps.add(propId);
    }

    this.scheduleSave();
    this.notifyPropsChanged();
    this.notifySaveStateChanged();

    return true;
  }

  /**
   * Move props to a different layer
   */
  movePropsToLayer(propIds: string[], targetLayerId: string): void {
    for (const propId of propIds) {
      const prop = this.props.get(propId);
      if (prop) {
        prop.layerId = targetLayerId;
        this.dirtyProps.add(propId);
      }
    }
    this.scheduleSave();
    this.notifyPropsChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Get props on a specific layer
   */
  getPropsOnLayer(layerId: string): PropInstance[] {
    return this.getProps().filter(p => (p.layerId || 'default') === layerId);
  }

  /**
   * Delete all props on specified layers
   * @param layerIds Array of layer IDs to delete props from
   */
  deletePropsOnLayers(layerIds: string[]): void {
    const layerIdSet = new Set(layerIds);
    const propsToDelete: string[] = [];

    for (const prop of this.props.values()) {
      const propLayerId = prop.layerId || 'default';
      if (layerIdSet.has(propLayerId)) {
        propsToDelete.push(prop.id);
      }
    }

    for (const propId of propsToDelete) {
      this.props.delete(propId);
      this.dirtyProps.delete(propId);

      if (this.newProps.has(propId)) {
        this.newProps.delete(propId);
      } else {
        this.deletedProps.add(propId);
      }
    }

    if (propsToDelete.length > 0) {
      this.scheduleSave();
      this.notifyPropsChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Move prop up or down in z-order
   */
  async changeLayer(propId: string, direction: 'up' | 'down'): Promise<void> {
    try {
      const response = await propsApi.changeLayer(this.mapId, propId, direction);
      const prop = this.props.get(propId);
      if (prop) {
        prop.zIndex = response.prop.zIndex;
        this.notifyPropsChanged();
      }
    } catch (err) {
      console.error('Failed to change prop layer:', err);
    }
  }

  /**
   * Find prop at a given world position
   */
  findPropAtPosition(worldX: number, worldY: number, tolerance: number = 10): PropInstance | null {
    // Iterate in reverse z-order (top to bottom)
    const sortedProps = this.getProps().reverse();

    for (const prop of sortedProps) {
      const img = this.propImages.get(prop.propName);
      if (!img) continue;

      const halfWidth = (img.width * prop.scale) / 2;
      const halfHeight = (img.height * prop.scale) / 2;

      // Simple bounding box check (doesn't account for rotation)
      const minX = prop.x - halfWidth - tolerance;
      const maxX = prop.x + halfWidth + tolerance;
      const minY = prop.y - halfHeight - tolerance;
      const maxY = prop.y + halfHeight + tolerance;

      if (worldX >= minX && worldX <= maxX && worldY >= minY && worldY <= maxY) {
        return prop;
      }
    }

    return null;
  }

  /**
   * Get the maximum z-index of all props
   */
  getMaxZIndex(): number {
    let maxZ = 0;
    for (const prop of this.props.values()) {
      maxZ = Math.max(maxZ, prop.zIndex || 0);
    }
    return maxZ;
  }

  /**
   * Schedule a debounced save (only if autosave is enabled)
   */
  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    if (this.autoSaveEnabled) {
      this.saveTimeout = setTimeout(() => {
        this.saveDirtyProps();
      }, PropManager.SAVE_DELAY);
    }
  }

  /**
   * Save all pending changes (new props, dirty props, deleted props)
   */
  async saveDirtyProps(): Promise<void> {
    if (this.dirtyProps.size === 0 && this.newProps.size === 0 && this.deletedProps.size === 0) return;

    // Run before-save callback (e.g., to save layers first and update local IDs)
    if (this.onBeforeSave) {
      await this.onBeforeSave();
    }

    this.isSaving = true;
    this.notifySaveStateChanged();

    try {
      // 1. Delete props marked for deletion
      for (const propId of this.deletedProps) {
        try {
          await propsApi.delete(this.mapId, propId);
        } catch (err) {
          console.error(`Failed to delete prop ${propId}:`, err);
        }
      }
      this.deletedProps.clear();

      // 2. Create new props
      const idMapping = new Map<string, string>(); // localId -> serverId
      for (const localId of this.newProps) {
        const prop = this.props.get(localId);
        if (prop) {
          try {
            const response = await propsApi.create(this.mapId, {
              propName: prop.propName,
              x: prop.x,
              y: prop.y,
              scale: prop.scale,
              rotation: prop.rotation,
              layerId: prop.layerId
            });

            // Replace local prop with server prop (new ID)
            this.props.delete(localId);
            const serverProp = { ...response.prop, regionX: response.regionX, regionY: response.regionY };

            // Copy any extra properties (scaleX, scaleY, etc.)
            const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
            if (propAny.scaleX !== undefined) (serverProp as typeof propAny).scaleX = propAny.scaleX;
            if (propAny.scaleY !== undefined) (serverProp as typeof propAny).scaleY = propAny.scaleY;

            this.props.set(serverProp.id, serverProp);
            idMapping.set(localId, serverProp.id);

            // If there are extra properties to save, mark as dirty
            if (propAny.scaleX !== undefined || propAny.scaleY !== undefined) {
              this.dirtyProps.add(serverProp.id);
            }
          } catch (err) {
            console.error(`Failed to create prop:`, err);
          }
        }
      }
      this.newProps.clear();

      // 3. Update dirty props
      const propsToSave: PropInstance[] = [];
      for (const propId of this.dirtyProps) {
        const prop = this.props.get(propId);
        if (prop) {
          propsToSave.push(prop);
        }
      }

      for (const prop of propsToSave) {
        try {
          await propsApi.update(this.mapId, prop.id, prop);
        } catch (err) {
          console.error(`Failed to update prop ${prop.id}:`, err);
        }
      }
      this.dirtyProps.clear();

      // Notify that props changed (IDs may have changed)
      this.notifyPropsChanged();
    } catch (err) {
      console.error('Failed to save props:', err);
    } finally {
      this.isSaving = false;
      this.notifySaveStateChanged();
    }
  }

  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.dirtyProps.size > 0 || this.newProps.size > 0 || this.deletedProps.size > 0;
  }

  /**
   * Notify that props have changed
   */
  private notifyPropsChanged(): void {
    this.onPropsChanged?.();
  }

  /**
   * Update layer IDs when layers get new server IDs
   * Called when LayerManager saves new layers and gets back server IDs
   */
  updateLayerIds(idMapping: Map<string, string>): void {
    let changed = false;
    for (const prop of this.props.values()) {
      if (prop.layerId && idMapping.has(prop.layerId)) {
        prop.layerId = idMapping.get(prop.layerId)!;
        this.dirtyProps.add(prop.id);
        changed = true;
      }
    }
    if (changed) {
      this.scheduleSave();
      this.notifyPropsChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }
}
